import { Component, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { DataserviceService } from '../dataservice.service';
import { PayrollResult } from '../services/payroll.service';
import { AppComponent } from '../app.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';

interface Rule {
  conditions: Condition[];
  then: {
    type: 'fixed' | 'logic';
    value: number;
    column?: string;
    operator?: string;
  };
  description: string;
}

interface Condition {
  condition: string;
  operator: string;
  value: any;
  logicOperator?: 'AND' | 'OR';
}

interface PayplanType {
  value: 'base' | 'incentive';
  label: string;
}

interface UserPayplanConflict {
  userId: string;
  userName: string;
  existingPayplan: {
    id: string;
    name: string;
    provider_id: string;
    start_date: string;
    end_date: string;
    type: 'base' | 'incentive';
  };
}

// Update the payroll results interface
interface PayrollRecordResult {
  fiberPayRecordId: string;
  workOrderId: string;
  customerId: string;
  customerName: string;
  commission: number;
  ruleDescription: string;
  fiberPayAmount: number;
  remainingAmount: number;
  paymentDate: Date;
  product: string;
  providerAccountNumber: string;
}

interface PayrollUserResult {
  userId: string;
  userName: string;
  payplanName: string;
  payplanId: string;
  totalCommission: number;
  records: PayrollRecordResult[];
}

@Component({
  selector: 'app-payplans',
  templateUrl: './payplans.component.html',
  styleUrls: ['./payplans.component.css']
})
export class PayplansComponent implements OnInit {

  addModal = false;
  payplans: any;
  payplansList: any;
  spinner: boolean = false;
  loadingUsers: boolean = false;
  states: any;
  valuesModal: boolean = false;
  displayStates: any[] = [];
  amount: any;
  projectColumns: string[] = [];
  showLogicBuilderModal: boolean = false;
  dynamicLogicRules: any[] = [];
  calculatedResults: PayrollResult[] = [];
  calculatingPayroll = false;
  selectedPayplanId: number | null = null;
  users: any[] = [];
  groups: any[] = []; // Add groups property
  selectedPayplan: any = null;
  editingRules: boolean = false;
  editingPayplanRules: Rule[] = [];

  currentUser: any;
  companies: any = [];
  selectedCompany: any;
  providers: any = [];
  planForm: FormGroup;
  ogProviders: any = [];
  ogUsers: any = [];
  ogGroups: any = []; // Add ogGroups property

  // Add new property for the toggle
  selectionMode: 'groups' | 'users' = 'users';

  // Add new properties for tracking conflicts
  userPayplanConflicts: UserPayplanConflict[] = [];
  showConflictModal = false;
  retroactive = false;
  fromDate: Date | null = null;
  rulesPreview: any = null;
  showRulesPreviewModal = false;
  pendingRetroAfterCreate = false;

  // Add new property for payplan types
  payplanTypes: PayplanType[] = [
    { value: 'base', label: 'Base Pay' },
    { value: 'incentive', label: 'Incentive' }
  ];

  constructor(private nzNotify: NzNotificationService,
              private dataService: DataserviceService,
              private app: AppComponent) {

                this.planForm = new FormGroup({
                  id: new FormControl(null),
                  name: new FormControl(null, [Validators.required]),
                  product: new FormControl(null),
                  provider: new FormControl(null, [Validators.required]),
                  dateRange: new FormControl(null, [Validators.required]),
                  users: new FormControl(null),
                  groups: new FormControl(null), // Add groups form control
                  company: new FormControl(null, [Validators.required]),
                  type: new FormControl('base', [Validators.required]), // Add type control with default value
                  holdback_enabled: new FormControl(false),
                  holdback_percentage: new FormControl(0),
                  holdback_days: new FormControl(0)
                })
              }

  ngOnInit(){
    this.currentUser = JSON.parse(localStorage.getItem('user')!);
    this.selectedCompany = this.currentUser?.company_id;

    this.spinner = true;
    if(this.hasPermission('view_all_payplans')) {
      this.getCompanies();
    }else if(this.hasPermission('view_distributors_payplans')) {
      this.getDistributorCompanies();
    }else {
      this.getAssociatedCompanyInfo();
    }

    this.states = this.dataService.states;
    this.getProjectColumns();

    // Add subscription to provider changes
    this.planForm.get('provider')?.valueChanges.subscribe(providerId => {
      if (!providerId) {
        // Clear selections without triggering additional events
        this.planForm.patchValue({
          users: null,
          groups: null
        }, { emitEvent: false });
      }
    });
  }

  hasPermission(permission: string): boolean {
    return this.app.hasPermission(permission);
  }

  getCompanies() {
    this.dataService.getData('company')
      .subscribe({
        next: (res: any) => {
          this.companies = res?.data;
          this.getAssociatedCompanyInfo()
        },
        error: (err: any) => {
          console.log('error on get all companies: ', err)
          this.errorNotification('Failed to get all companies')
        }
      })
  }

  getDistributorCompanies() {
    this.dataService.getData(`company/companies-by-distributor/${this.currentUser?.distributor_id}`)
      .subscribe({
        next: (res: any) => {
          this.companies = res?.data;
          this.getAssociatedCompanyInfo();
        },
        error: (err: any) => {
          console.log('error on get companies by distributor: ', err)
          this.errorNotification('Failed to get companies by distributor')
        }
    });
  }

  getAssociatedCompanyInfo() {
    this.dataService.getData(`company/associated-company-info/${this.selectedCompany}/true`) // Changed to true to get users
      .subscribe({
        next: (res: any) => {
          console.log('associated company info: ', res)
          this.users = res?.data?.users;
          this.ogUsers = res?.data?.users;
          this.groups = res?.data?.groups; // Add groups
          this.ogGroups = res?.data?.groups; // Add ogGroups
          this.providers = res?.data?.providers;
          this.ogProviders = res?.data?.providers;
          this.payplans = res?.data?.payplans;
          this.payplansList = res?.data?.payplans;
          this.spinner = false;
        },
        error: (err: any) => {
          console.log('error on getUsersByCompanyId: ', err)
          this.errorNotification('Failed to get users by selected company')
        }
    });
  }

  getUsersandProviders() {
    if(this.planForm.get('company')?.value) {
      this.loadingUsers = true;
      this.dataService.getData(`company/associated-company-info/${this.planForm.get('company')?.value}/true`) // Changed to true to get users
      .subscribe({
        next: (res: any) => {
          console.log('associated company info: ', res)
          this.users = res?.data?.users;
          this.groups = res?.data?.groups; // Add groups
          this.providers = res?.data?.providers;
          this.loadingUsers = false;
        },
        error: (err: any) => {
          console.log('error on getUsersByCompanyId: ', err)
          this.errorNotification('Failed to get users by selected company')
        }
      });
    }
  }

  // Add method to get users from selected groups
  getUsersFromGroups(groupIds: string[]): string[] {
    if (!groupIds || groupIds.length === 0) return [];
    
    const userIds: string[] = [];
    groupIds.forEach(groupId => {
      const group = this.groups.find(g => g.id === groupId);
      if (group && group.users && Array.isArray(group.users)) {
        group.users.forEach((user: any) => {
          if (!userIds.includes(user.id)) {
            userIds.push(user.id);
          }
        });
      }
    });
    return userIds;
  }

  showModal(pp: any){
    console.log('pp: ', pp)
    this.addModal = true;
    this.showLogicBuilderModal = false;
    if(pp) {
      // First get the full payplan data with users
      this.dataService.getData(`payplans/${pp.id}`).subscribe({
        next: (payplanData: any) => {
          console.log('Full payplan data:', payplanData);
          
          // Then get the company's users and groups
          this.dataService.getData(`company/associated-company-info/${pp.company_id}/true`)
            .subscribe({
              next: (res: any) => {
                this.users = res?.data?.users;
                this.groups = res?.data?.groups;
                
                // Now set the form values
                this.planForm.patchValue({
                  id: pp.id,
                  name: pp.name,
                  provider: pp.provider_id,
                  company: pp.company_id,
                  dateRange: [new Date(pp.start_date), new Date(pp.end_date)],
                  product: pp.product_type,
                  type: pp.type || 'base',
                  holdback_enabled: !!pp.holdback_enabled,
                  holdback_percentage: pp.holdback_percentage || 0,
                  holdback_days: pp.holdback_days || 0
                }, { emitEvent: false });

                // Get the users assigned to this payplan
                if (payplanData.users && Array.isArray(payplanData.users)) {
                  const userIds = payplanData.users.map((user: any) => user.id);
                  
                  // Find which groups contain these users
                  const groupsWithUsers = this.groups.filter(group => {
                    if (!group.users || !Array.isArray(group.users)) return false;
                    return group.users.some((user: any) => userIds.includes(user.id));
                  });

                  if (groupsWithUsers.length > 0) {
                    // If all users belong to groups, set to groups mode
                    const groupIds = groupsWithUsers.map(g => g.id);
                    const allUsersInGroups = userIds.every((userId: string) => 
                      groupsWithUsers.some(group => 
                        group.users.some((user: any) => user.id === userId)
                      )
                    );

                    if (allUsersInGroups) {
                      this.selectionMode = 'groups';
                      this.planForm.patchValue({ groups: groupIds }, { emitEvent: false });
                    } else {
                      this.selectionMode = 'users';
                      this.planForm.patchValue({ users: userIds }, { emitEvent: false });
                    }
                  } else {
                    // If no groups contain these users, set to users mode
                    this.selectionMode = 'users';
                    this.planForm.patchValue({ users: userIds }, { emitEvent: false });
                  }
                }
              },
              error: (err: any) => {
                console.log('error getting company info:', err);
                this.errorNotification('Failed to get company information');
              }
            });
        },
        error: (err: any) => {
          console.log('error getting payplan details:', err);
          this.errorNotification('Failed to get payplan details');
        }
      });
    } else {
      this.planForm.patchValue({
        company: this.selectedCompany
      }, { emitEvent: false });
      this.retroactive = false;
      this.fromDate = null;
      this.rulesPreview = null;
      this.showRulesPreviewModal = false;
      this.pendingRetroAfterCreate = false;
    }
  }

  addPlan(){
    let userIds: string[] = [];
    
    if (this.selectionMode === 'users') {
      userIds = this.planForm.get('users')?.value || [];
    } else {
      const selectedGroups = this.planForm.get('groups')?.value || [];
      userIds = this.getUsersFromGroups(selectedGroups);
    }
  
    // Final conflict check before saving
    const providerId = this.planForm.get('provider')?.value;
    this.checkPayplanConflicts(userIds, providerId);
    
    if (this.userPayplanConflicts.length > 0) {
      return; // Don't proceed if there are conflicts
    }
  
    const obj: any = {
      name: this.planForm.get('name')?.value,
      type: this.planForm.get('type')?.value as PayplanType,
      start_date: this.planForm.get('dateRange')?.value[0].toISOString(),
      end_date: this.planForm.get('dateRange')?.value[1].toISOString(),
      states: this.displayStates,
      rules: this.dynamicLogicRules,
      userIds: userIds,
      product_type: this.planForm.get('product')?.value,
      company_id: this.planForm.get('company')?.value,
      provider_id: providerId,
      holdback_enabled: this.planForm.get('holdback_enabled')?.value,
      holdback_percentage: this.planForm.get('holdback_percentage')?.value,
      holdback_days: this.planForm.get('holdback_days')?.value
    };
  
    if (this.retroactive && this.fromDate) {
      obj.retroactive = true;
      obj.fromDate = this.fromDate.toISOString();
    }
  
    this.dataService.postData(obj, 'payplans/create')
      .subscribe({
        next: (res: any) => {
          if (this.retroactive) {
            // Preview response, no plan created yet
            this.rulesPreview = res?.data || res || null;
            this.pendingRetroAfterCreate = true;
            this.selectedPayplan = null; // no id yet
            this.editingPayplanRules = [...this.dynamicLogicRules];
            this.showRulesPreviewModal = true;
            this.spinner = false;
            return;
          }
  
          // Non-retroactive: created immediately
          this.getAssociatedCompanyInfo();
          this.successNotification('created');
          this.cancelModal();
        },
        error: (error) => {
          this.errorNotification('Failed to addPlan')
          console.error('Error creating payplan:', error);
        }
      });
  };

  saveModal() {
    if(this.planForm.valid) {
      this.spinner = true;
      if(this.planForm.get('id')?.value) {
        this.updatePayplan();
      }else {
        this.addPlan()
      }
    }else {
      this.nzNotify.create(
        'warning',
        'Warning!',
        `Please fill out the required fields and make sure they are formatted correctly`,
        { nzPlacement: 'top' }
      );
    }
  }

  cancelModal() {
    this.planForm.reset({}, { emitEvent: false });
    this.addModal = false;
    this.dynamicLogicRules = [];
    this.showLogicBuilderModal = false;
    this.users = this.ogUsers;
    this.providers = this.ogProviders;
    this.selectionMode = 'users'; // Reset selection mode
  }


  search(event: any){
    this.payplansList = this.payplans.filter((item: any) => item.name.toLowerCase().indexOf(event.target.value.toLowerCase()) !== -1);
  }

  showLogicBuilder() {
    this.showLogicBuilderModal = true;
  }

  catchInfo(e:any){
    let a = [];
    for(let i = 0; i < e.states.length; i++){
      if(e.states[i].checked && !e.states[i].disabled){
        a.push(e.states[i]);
        e.states[i].disabled = true;
      }
    }
    this.displayStates.push({amount: e.amount, states: a});
    this.states = e.states;
    this.valuesModal = false;
  }

  closedModal(e: any) {
    this.states = e
    this.valuesModal = false;
  }

  editPPW(e:any){
    for(let i = 0; i < e.states.length; i++){
      const pos = this.states.map((s:any) => s.label).indexOf(e.states[i].label);
      this.states[pos].disabled = false;
    }
    this.amount = e.amount;
    // this.states = e.states;
    this.valuesModal = true;
  }

  getProjectColumns() {
    this.dataService.getData('customers/columns')
    .subscribe((columnNames: string[]) => {
      this.projectColumns = columnNames;
      if (!this.projectColumns.includes('user_id')) this.projectColumns.push('user_id');
    });
    // this.projectColumns = ['column1', 'column2', 'column3'];
  }

  onLogicBuilt(rule: any) {
    if (this.editingRules) {
      this.editingPayplanRules.push(rule);
    } else {
      this.dynamicLogicRules.push(rule);
    }
    this.showLogicBuilderModal = false;
  }

  editLogicRule(index: number) {
    // Implement edit functionality if needed
    this.showLogicBuilderModal = true;
    // You might want to pass the existing rule to the logic builder for editing
  }

  deleteLogicRule(index: number) {
    if (this.editingRules) {
      this.editingPayplanRules.splice(index, 1);
    } else {
      this.dynamicLogicRules.splice(index, 1);
    }
  }

  // calculatePayroll(payplanId: number) {
  //   this.calculatingPayroll = true;
  //   this.selectedPayplanId = payplanId;

  //   // this.dataService.calculatePayroll(payplanId).subscribe({
  //   //   next: (response) => {
  //   //     this.calculatedResults = response.results;
  //   //     this.message.success('Payroll calculated successfully');
  //   //     this.calculatingPayroll = false;
  //   //   },
  //   //   //   error: (error) => {
  //   //   //     this.message.error('Failed to calculate payroll');
  //   //   //     this.calculatingPayroll = false;
  //   //   //     console.error('Error calculating payroll:', error);
  //   //   //   }
  //   //   // });
  //   // }

  // viewPayrollResults(payplanId: number) {
  //   this.selectedPayplanId = payplanId;
  //   this.calculatingPayroll = true;

  //   this.dataService.getPayrollResults(payplanId).subscribe({
  //     next: (response) => {
  //       this.calculatedResults = response.results;
  //       this.calculatingPayroll = false;
  //     },
  //     error: (error) => {
  //       this.message.error('Failed to fetch payroll results');
  //       this.calculatingPayroll = false;
  //       console.error('Error fetching payroll results:', error);
  //     }
  //   });
  // }

  getGrandTotal(): number {
    return this.calculatedResults.reduce((total, userResult) => 
      total + userResult.totalCommission, 0);
  }

  togglePayplanActive(payplanId: string, event: MouseEvent) {
    this.spinner = true;
    this.dataService.patch(`payplans/${payplanId}/toggle-active`, {}).subscribe({
      next: (response) => {
        this.successNotification('updated')
        this.getAssociatedCompanyInfo(); // Refresh the list
      },
      error: (error) => {
        this.errorNotification('Failed to update plan status')
        console.error('Error updating payplan status:', error);
      }
    });
  }

  updatePayplan() {
    let userIds: string[] = [];
    
    if (this.selectionMode === 'users') {
      userIds = this.planForm.get('users')?.value || [];
      console.log('userIds: ', userIds)
    } else {
      const selectedGroups = this.planForm.get('groups')?.value || [];
      userIds = this.getUsersFromGroups(selectedGroups);
    }

    const obj = {
      name: this.planForm.get('name')?.value,
      type: this.planForm.get('type')?.value,
      start_date: this.planForm.get('dateRange')?.value[0].toISOString(),
      end_date: this.planForm.get('dateRange')?.value[1].toISOString(),
      userIds: userIds,
      product_type: this.planForm.get('product')?.value,
      provider_id: this.planForm.get('provider')?.value,
      company_id: this.planForm.get('company')?.value,
      holdback_enabled: this.planForm.get('holdback_enabled')?.value,
      holdback_percentage: this.planForm.get('holdback_percentage')?.value,
      holdback_days: this.planForm.get('holdback_days')?.value
    };

    this.dataService.post(`payplans/${this.planForm.get('id')?.value}`, obj).subscribe({
      next: (response) => {
        this.successNotification('updated')
        this.cancelModal()
        this.getAssociatedCompanyInfo();
      },
      error: (error) => {
        this.errorNotification('Failed to updatePlan')
        console.error('Error updating payplan:', error);
      }
    });
  }

  showRuleEditor(payplan: any) {
    this.selectedPayplan = payplan;
    this.editingRules = true;
    this.editingPayplanRules = [...(payplan.rules || [])];
    this.showLogicBuilderModal = true;
    this.retroactive = false;
    this.fromDate = null;
    this.rulesPreview = null;
    this.showRulesPreviewModal = false;
  }

  savePayplanRules() {
    if (!this.selectedPayplan) return;

    this.spinner = true;
    this.dataService.patch(
      `payplans/${this.selectedPayplan.id}/rules`,
      {
        rules: this.editingPayplanRules,
        retroactive: this.retroactive,
        fromDate: this.retroactive && this.fromDate ? this.fromDate.toISOString() : undefined
      }
    ).subscribe({
      next: (response: any) => {
        this.rulesPreview = response?.data || null;

        if (!this.retroactive) {
          // Auto-approve immediately when not retroactive
          this.confirmApproveRules();
          return;
        }

        this.showRulesPreviewModal = true; // show confirmation modal
        this.spinner = false;
      },
      error: (error) => {
        this.spinner = false;
        this.errorNotification('Failed to preview payplan rules');
        console.error('Error previewing payplan rules:', error);
      }
    });
  }

  confirmApproveRules() {
    this.spinner = true;
  
    // If we're approving a new retroactive plan creation
    if (this.pendingRetroAfterCreate) {
      // Recompute userIds based on current selection
      let userIds: string[] = [];
      if (this.selectionMode === 'users') {
        userIds = this.planForm.get('users')?.value || [];
      } else {
        const selectedGroups = this.planForm.get('groups')?.value || [];
        userIds = this.getUsersFromGroups(selectedGroups);
      }
  
      const payload: any = {
        name: this.planForm.get('name')?.value,
        type: this.planForm.get('type')?.value,
        start_date: this.planForm.get('dateRange')?.value[0].toISOString(),
        end_date: this.planForm.get('dateRange')?.value[1].toISOString(),
        states: this.displayStates,
        rules: this.dynamicLogicRules,
        userIds,
        product_type: this.planForm.get('product')?.value,
        provider_id: this.planForm.get('provider')?.value,
        company_id: this.planForm.get('company')?.value,
        holdback_enabled: this.planForm.get('holdback_enabled')?.value,
        holdback_percentage: this.planForm.get('holdback_percentage')?.value,
        holdback_days: this.planForm.get('holdback_days')?.value,
        retroactive: this.retroactive,
        fromDate: this.retroactive && this.fromDate ? this.fromDate.toISOString() : undefined,
        approverId: this.currentUser?.id
      };
  
      this.dataService.post('payplans/create/approve', payload).subscribe({
        next: () => {
          this.successNotification('created');
          this.showRulesPreviewModal = false;
          this.pendingRetroAfterCreate = false;
          this.cancelModal();
          this.getAssociatedCompanyInfo();
          this.spinner = false;
        },
        error: (error) => {
          this.spinner = false;
          this.errorNotification('Failed to approve and create payplan');
          console.error('Error approving new payplan:', error);
        }
      });
      return;
    }
  
    // Existing rule-edit approval path
    if (!this.selectedPayplan) { this.spinner = false; return; }
  
    this.dataService.post(
      `payplans/${this.selectedPayplan.id}/rules/approve`,
      {
        rules: this.editingPayplanRules,
        approverId: this.currentUser?.id,
        retroactive: this.retroactive,
        fromDate: this.retroactive && this.fromDate ? this.fromDate.toISOString() : undefined
      }
    ).subscribe({
      next: () => {
        this.successNotification('updated');
        this.editingRules = false;
        this.showRulesPreviewModal = false;
        this.getAssociatedCompanyInfo();
        this.spinner = false;
      },
      error: (error) => {
        this.spinner = false;
        this.errorNotification('Failed to approve payplan rules');
        console.error('Error approving payplan rules:', error);
      }
    });
  }

  successNotification(str: string) {
    this.nzNotify.create(
      'success',
      'Success!',
      `Payplan was ${str} successfully!`,
      { nzPlacement: 'top' }
    );
  }


  errorNotification(str: string) {
    this.nzNotify.create(
      'error',
      'Error!',
      str,
      { nzPlacement: 'top' }
    );
  }

  // Add method to handle toggle change
  onSelectionModeChange(mode: 'groups' | 'users') {
    this.selectionMode = mode;
    // Add emitEvent: false to prevent triggering ngModelChange
    this.planForm.patchValue({
      users: null,
      groups: null
    }, { emitEvent: false });
  }

  // Update the conflict checking logic
  checkPayplanConflicts(userIds: string[], providerId: string): void {
    this.userPayplanConflicts = [];
    const newStartDate = this.planForm.get('dateRange')?.value?.[0];
    const newEndDate = this.planForm.get('dateRange')?.value?.[1];
    const payplanType = this.planForm.get('type')?.value;

    // Skip conflict check for incentive payplans
    if (payplanType === 'incentive') {
      return;
    }
    
    userIds.forEach(userId => {
      const user = this.users.find(u => u.id === userId);
      if (user && user.payplans) {
        const conflictingPayplans = user.payplans.filter((pp: any) => {
          // Only check base payplans
          if (pp.type !== 'base') {
            return false;
          }

          // Check if payplan is for the same provider and is active
          if (pp.provider_id !== providerId || !pp.active) {
            return false;
          }

          // Check date overlap
          const ppStart = new Date(pp.start_date);
          const ppEnd = new Date(pp.end_date);
          
          return (
            (newStartDate >= ppStart && newStartDate <= ppEnd) || // New start date falls within existing plan
            (newEndDate >= ppStart && newEndDate <= ppEnd) || // New end date falls within existing plan
            (newStartDate <= ppStart && newEndDate >= ppEnd) // New plan completely encompasses existing plan
          );
        });
        
        if (conflictingPayplans.length > 0) {
          conflictingPayplans.forEach((conflictingPayplan: any) => {
            this.userPayplanConflicts.push({
              userId: userId,
              userName: `${user.first_name} ${user.last_name}`,
              existingPayplan: {
                id: conflictingPayplan.id,
                name: conflictingPayplan.name,
                provider_id: conflictingPayplan.provider_id,
                start_date: conflictingPayplan.start_date,
                end_date: conflictingPayplan.end_date,
                type: conflictingPayplan.type
              }
            });
          });
        }
      }
    });

    if (this.userPayplanConflicts.length > 0) {
      this.showConflictModal = true;
    }
  }

  // Add method to edit dates of current payplan
  editCurrentPayplanDates(): void {
    // Close conflict modal but keep form open for editing
    this.showConflictModal = false;
  }

  // Modify the existing method to include conflict checking
  onUserOrGroupSelection(): void {
    const providerId = this.planForm.get('provider')?.value;
    if (!providerId) {
      this.nzNotify.warning(
        'Warning',
        'Please select a provider before selecting users or groups',
        { nzPlacement: 'top' }
      );
      return;
    }

    let userIds: string[] = [];
    if (this.selectionMode === 'users') {
      userIds = this.planForm.get('users')?.value || [];
    } else {
      const selectedGroups = this.planForm.get('groups')?.value || [];
      userIds = this.getUsersFromGroups(selectedGroups);
    }

    if (userIds.length > 0) {
      this.checkPayplanConflicts(userIds, providerId);
    }
  }

  // Add method to handle editing existing payplan
  editExistingPayplan(payplanId: string): void {
    const existingPayplan = this.payplans.find((pp: any) => pp.id === payplanId);
    if (existingPayplan) {
      this.showConflictModal = false;
      this.showModal(existingPayplan);
    }
  }

  // Add method to continue with new payplan
  continueWithNewPayplan(): void {
    this.showConflictModal = false;
  }
}